import { useEffect, useState } from "react";
import { ApiError, request } from "./api";

type ProbeCounters = {
  reports: number;
  validNumbers: number;
  matched: number;
  noMatch: number;
  ambiguous: number;
  unavailable: number;
  rateLimited: number;
};
type ProbeReasons = {
  hidden_document: number;
  panel_missing_or_ambiguous: number;
  phone_missing_or_ambiguous: number;
  invalid_number: number;
  lookup_unavailable: number;
};
type PageContext = {
  contact_detail: number;
  contacts_list: number;
  conversations: number;
  other: number;
  contactPageVisible: number;
  contactPageFreshBusy: number;
  contactPageFreshBusySingleTab: number;
  checks: number;
  verified: number;
  unavailable: number;
  rateLimited: number;
  nameAvailable: number;
  phoneAvailable: number;
};
type ProbeSummary = {
  userCount: number;
  expiresAt: number;
  counters: ProbeCounters;
  reasons: ProbeReasons;
  pageContext?: PageContext;
};
const counterNames: (keyof ProbeCounters)[] = [
  "reports",
  "validNumbers",
  "matched",
  "noMatch",
  "ambiguous",
  "unavailable",
  "rateLimited",
];
const reasonLabels: { key: keyof ProbeReasons; label: string }[] = [
  { key: "hidden_document", label: "Hidden page" },
  { key: "panel_missing_or_ambiguous", label: "No single call panel" },
  { key: "phone_missing_or_ambiguous", label: "No single phone field" },
  { key: "invalid_number", label: "Number lacks usable country code" },
  { key: "lookup_unavailable", label: "Lookup unavailable" },
];
const pageContextKeys: (keyof PageContext)[] = [
  "contact_detail",
  "contacts_list",
  "conversations",
  "other",
  "contactPageVisible",
  "contactPageFreshBusy",
  "contactPageFreshBusySingleTab",
  "checks",
  "verified",
  "unavailable",
  "rateLimited",
  "nameAvailable",
  "phoneAvailable",
];
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const isCounters = (value: unknown): value is ProbeCounters =>
  isRecord(value) && counterNames.every((name) => isCount(value[name]));
const isReasons = (value: unknown): value is ProbeReasons =>
  isRecord(value) && reasonLabels.every(({ key }) => isCount(value[key]));
const isPageContext = (value: unknown): value is PageContext =>
  isRecord(value) && pageContextKeys.every((key) => isCount(value[key]));

function parseSummary(value: unknown): ProbeSummary | null {
  if (!isRecord(value) || value.enabled !== true) return null;
  if (
    !isCount(value.userCount) ||
    typeof value.expiresAt !== "number" ||
    !Number.isFinite(value.expiresAt) ||
    value.expiresAt <= Date.now() ||
    !isCounters(value.counters) ||
    !isReasons(value.reasons)
  )
    return null;
  return {
    userCount: value.userCount,
    expiresAt: value.expiresAt,
    counters: value.counters,
    reasons: value.reasons,
    ...(isPageContext(value.pageContext)
      ? { pageContext: value.pageContext }
      : {}),
  };
}

export function ActiveCallProbeStatus() {
  const [summary, setSummary] = useState<ProbeSummary | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);

  useEffect(() => {
    let current = true;
    let inFlight = false;
    let timer: number | undefined;
    let expiryTimer: number | undefined;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await request<unknown>(
          "GET",
          "/api/admin/active-call-probe",
        );
        if (!current) return;
        const next = parseSummary(response);
        window.clearTimeout(expiryTimer);
        if (next)
          expiryTimer = window.setTimeout(
            () => setSummary(null),
            Math.max(0, next.expiresAt - Date.now()),
          );
        setSummary(next);
        setRefreshFailed(false);
      } catch (error) {
        if (!current) return;
        if (error instanceof ApiError && [401, 403].includes(error.status)) {
          setSummary(null);
          setRefreshFailed(false);
          window.clearInterval(timer);
          window.clearTimeout(expiryTimer);
        } else {
          setRefreshFailed(true);
        }
      } finally {
        inFlight = false;
      }
    };
    void load();
    timer = window.setInterval(() => void load(), 15_000);
    return () => {
      current = false;
      window.clearInterval(timer);
      window.clearTimeout(expiryTimer);
    };
  }, []);

  if (!summary) return null;
  return (
    <section className="context-section" aria-label="Contact lookup trial">
      <h2>Contact lookup trial</h2>
      <p className="meta">
        {summary.userCount} people in trial · Ends{" "}
        {new Date(summary.expiresAt).toLocaleString([], {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </p>
      {refreshFailed && (
        <p className="field-hint" role="status">
          Refresh unavailable. Counts last reported.
        </p>
      )}
      <dl className="detail-facts">
        <div>
          <dt>Reports received</dt>
          <dd>{summary.counters.reports}</dd>
        </div>
        <div>
          <dt>Valid phone observations</dt>
          <dd>{summary.counters.validNumbers}</dd>
        </div>
        <div>
          <dt>Matched lookups</dt>
          <dd>{summary.counters.matched}</dd>
        </div>
        <div>
          <dt>No-match lookups</dt>
          <dd>{summary.counters.noMatch}</dd>
        </div>
        <div>
          <dt>Ambiguous lookups</dt>
          <dd>{summary.counters.ambiguous}</dd>
        </div>
        <div>
          <dt>Unavailable reports/lookups</dt>
          <dd>{summary.counters.unavailable}</dd>
        </div>
        <div>
          <dt>Rate-limited reports</dt>
          <dd>{summary.counters.rateLimited}</dd>
        </div>
      </dl>
      {reasonLabels.some(({ key }) => summary.reasons[key] > 0) && (
        <>
          <p className="meta">Unavailable reasons</p>
          <dl className="detail-facts">
            {reasonLabels
              .filter(({ key }) => summary.reasons[key] > 0)
              .map(({ key, label }) => (
                <div key={key}>
                  <dt>{label}</dt>
                  <dd>{summary.reasons[key]}</dd>
                </div>
              ))}
          </dl>
        </>
      )}
      {summary.pageContext && (
        <>
          <h3>Open contact page</h3>
          <p className="meta">
            An open contact page does not confirm who is on the call.
          </p>
          <dl className="detail-facts">
            <div>
              <dt>Contact page observations</dt>
              <dd>{summary.pageContext.contact_detail}</dd>
            </div>
            <div>
              <dt>Visible contact page</dt>
              <dd>{summary.pageContext.contactPageVisible}</dd>
            </div>
            <div>
              <dt>With fresh Busy observation</dt>
              <dd>{summary.pageContext.contactPageFreshBusy}</dd>
            </div>
            <div>
              <dt>With one GHL tab</dt>
              <dd>{summary.pageContext.contactPageFreshBusySingleTab}</dd>
            </div>
            <div>
              <dt>API contact checks</dt>
              <dd>{summary.pageContext.checks}</dd>
            </div>
            <div>
              <dt>Verified contact records</dt>
              <dd>{summary.pageContext.verified}</dd>
            </div>
            <div>
              <dt>Name available</dt>
              <dd>{summary.pageContext.nameAvailable}</dd>
            </div>
            <div>
              <dt>Phone available</dt>
              <dd>{summary.pageContext.phoneAvailable}</dd>
            </div>
            <div>
              <dt>API unavailable</dt>
              <dd>{summary.pageContext.unavailable}</dd>
            </div>
            <div>
              <dt>Checks rate limited</dt>
              <dd>{summary.pageContext.rateLimited}</dd>
            </div>
          </dl>
        </>
      )}
    </section>
  );
}
